import { t } from '@core/i18n';
import { LettingTransactionsShowItem } from '@shared/apis/transactionsLettingsApi.types';
import { SaleTransactionsShowItem } from '@shared/apis/transactionsSalesApi.types';
import { MixedUser } from '@shared/components/lookups/user-lookup';
import { IPublishToAtlasDetails } from '@shared/models/transactions/transactions';

import { FieldToggleItem } from '../components/atlas-transaction-form/components/FieldToggleList/FieldToggleList.types';

export const getLettingsAtlasPublishFields = (): FieldToggleItem[] => [
  {
    key: 'headline_rent',
    displayName: t('headline_rent'),
    defaultVisible: true,
  },
  {
    key: 'lease_length',
    displayName: t('lease_length'),
    defaultVisible: true,
  },
  {
    key: 'lease_end_date',
    displayName: t('lease_end_date'),
    defaultVisible: true,
  },
  {
    key: 'rent_per_floor_unit',
    displayName: t('rent_per_floor_unit_1'),
    defaultVisible: true,
  },
  {
    key: 'rent_free_periods',
    displayName: t('rent_free_periods'),
    defaultVisible: true,
  },
  {
    key: 'lease_landlord',
    displayName: t('landlord'),
    defaultVisible: true,
  },
  {
    key: 'lease_tenant',
    displayName: t('tenant'),
    defaultVisible: true,
  },
  {
    key: 'lease_breaks',
    displayName: t('lease_breaks'),
    defaultVisible: true,
  },
  {
    key: 'rent_review_date',
    displayName: t('rent_review_date_1'),
    defaultVisible: true,
  },
  {
    key: 'capital_contributions',
    displayName: t('capital_incentives_1'),
    defaultVisible: true,
  },
];

export const isSalesTransaction = (
  transaction: LettingTransactionsShowItem | SaleTransactionsShowItem
): transaction is SaleTransactionsShowItem => {
  return 'purchaser_agent_users' in transaction;
};

export const convertAgentIdsToUsers = (
  agentIds: number[],
  transaction: LettingTransactionsShowItem | SaleTransactionsShowItem,
  fieldKey: string
): MixedUser[] => {
  const uniqueIds = [...new Set(agentIds)];

  const sourceUsers = (() => {
    if (fieldKey === 'tenant_agent' || fieldKey === 'purchaser_agent') {
      if (isSalesTransaction(transaction)) {
        return transaction.purchaser_agent_users || [];
      } else {
        return transaction.tenant_agent_users || [];
      }
    } else {
      if (isSalesTransaction(transaction)) {
        return transaction.vendor_agent_users || [];
      } else {
        return transaction.landlord_agent_users || [];
      }
    }
  })();

  const validUsers: MixedUser[] = [];

  uniqueIds.forEach((id) => {
    const user = sourceUsers.find((u) => u.id === id);
    if (user) {
      validUsers.push({
        id: user.id,
        name: user.name,
      });
    }
  });

  return validUsers;
};

export const convertUsersToAgentIds = (users: MixedUser[]): number[] => {
  return users.filter((user): user is MixedUser & { id: number } => 'id' in user).map((user) => user.id);
};

export const getSalesAtlasPublishFieldsInvestment = (): FieldToggleItem[] => [
  {
    key: 'purchase_price',
    displayName: t('price'),
    defaultVisible: true,
  },
  {
    key: 'vendor_company',
    displayName: t('vendor'),
    defaultVisible: true,
  },
  {
    key: 'purchaser_company',
    displayName: t('purchaser'),
    defaultVisible: true,
  },
  {
    key: 'passing_giy',
    displayName: t('gross_initial_yield'),
    defaultVisible: true,
  },
  {
    key: 'passing_niy',
    displayName: t('net_initial_yield'),
    defaultVisible: true,
  },
  {
    key: 'passing_rent',
    displayName: t('passing_rent'),
    defaultVisible: true,
  },
  {
    key: 'wault',
    displayName: t('wault_1'),
    defaultVisible: true,
  },
];

export const getSalesAtlasPublishFieldsOccupational = (): FieldToggleItem[] => [
  {
    key: 'purchase_price',
    displayName: t('price'),
    defaultVisible: true,
  },
  {
    key: 'vendor_company',
    displayName: t('vendor'),
    defaultVisible: true,
  },
  {
    key: 'purchaser_company',
    displayName: t('purchaser'),
    defaultVisible: true,
  },
];

export const convertAtlasDetailsToFieldVisibility = (
  details: Record<string, IPublishToAtlasDetails> | undefined
): Record<string, boolean> => {
  if (!details) return {};

  const result: Record<string, boolean> = {};
  Object.keys(details).forEach((key) => {
    result[key] = details[key]?.enabled ?? false;
  });
  return result;
};

export const getFieldDisplayName = (fieldKey: string, transactionType: 'lettings' | 'sales'): string => {
  const fields =
    transactionType === 'lettings' ? getLettingsAtlasPublishFields() : getSalesAtlasPublishFieldsInvestment(); // TOOD: check if this is correct as there are 2 types of sales fields but only one is used here

  const field = fields.find((f) => f.key === fieldKey);
  return field?.displayName || fieldKey;
};

export type FieldVisibilityStatus = 'hidden' | 'shared' | null;

interface PublishToAtlasDetails {
  [key: string]: {
    enabled?: boolean;
  };
}

interface TransactionWithPublishDetails {
  publish_to_atlas?: {
    details?: PublishToAtlasDetails;
  };
}

export const getFieldVisibilityStatus = <T extends TransactionWithPublishDetails>(
  fieldKey: string,
  transaction: T,
  fieldToPublishKeyMap: Record<string, string>,
  fieldVisibility?: Record<string, boolean>
): FieldVisibilityStatus => {
  const publishFieldKey = fieldToPublishKeyMap[fieldKey];
  if (!publishFieldKey) {
    return 'shared';
  }

  if (fieldVisibility !== undefined) {
    const isVisible = fieldVisibility[publishFieldKey];
    if (isVisible !== undefined) {
      return isVisible ? 'shared' : 'hidden';
    }
    return 'shared';
  }

  const details = transaction.publish_to_atlas?.details;
  if (!details) {
    return 'shared';
  }

  const fieldDetails = details[publishFieldKey];
  if (!fieldDetails) {
    return 'shared';
  }

  return fieldDetails.enabled === false ? 'hidden' : 'shared';
};
